import * as React from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button } from '@workspace/ui/components/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@workspace/ui/components/tabs';
import { checkWorkspaceEntitlement } from '@/billing/billing.functions';
import { UpgradePromptDialog } from '@/components/billing/upgrade-prompt-dialog';
import { WorkspaceInvitationsTable } from '@/components/workspace/workspace-invitations-table';
import { WorkspaceInviteDialog } from '@/components/workspace/workspace-invite-dialog';
import { WorkspaceMembersTable } from '@/components/workspace/workspace-members-table';
import { useUpgradePrompt } from '@/hooks/use-upgrade-prompt';
import { getWorkspaceCapabilities } from '@/policy/workspace-capabilities.functions';
import { getWorkspaceLifecycleCapabilities } from '@/policy/workspace-lifecycle-capabilities.functions';
import { useInvitationsTable } from '@/workspace/use-invitations-table';
import { useMembersTable } from '@/workspace/use-members-table';
import { DEFAULT_INVITE_ROLES } from '@/workspace/workspace-members.types';

export const Route = createFileRoute('/_protected/ws/$workspaceId/members')({
  loader: async ({ params }) => {
    const [capabilities, lifecycle] = await Promise.all([
      getWorkspaceCapabilities({
        data: { workspaceId: params.workspaceId },
      }),
      getWorkspaceLifecycleCapabilities({
        data: { workspaceId: params.workspaceId },
      }),
    ]);

    if (!capabilities.canViewMembers) {
      throw notFound({ routeId: '__root__' });
    }

    return {
      ...capabilities,
      canLeaveWorkspace: lifecycle.canLeaveWorkspace,
    };
  },
  component: WorkspaceMembersPage,
  staticData: { title: 'Members' },
});

function WorkspaceMembersPage() {
  const { workspaceId } = Route.useParams();
  const [activeTab, setActiveTab] = React.useState<'members' | 'invitations'>(
    'members'
  );
  const capabilities = Route.useLoaderData();

  const { ...membersTableProps } = useMembersTable(
    workspaceId,
    capabilities.workspaceRole,
    capabilities.canLeaveWorkspace
  );

  const { inviteDialog, ...invitationsTableProps } =
    useInvitationsTable(workspaceId);

  const canInvite = capabilities.canInviteMembers;
  const membersTablePropsWithRole = {
    ...membersTableProps,
    workspaceRole: capabilities.workspaceRole,
    canManageMembers: capabilities.canManageMembers,
  };

  const upgradePrompt = useUpgradePrompt(workspaceId);

  const handleInviteClick = async () => {
    try {
      const result = await checkWorkspaceEntitlement({
        data: { workspaceId, key: 'members' },
      });
      if (result.allowed) {
        inviteDialog.onOpenChange(true);
      } else {
        upgradePrompt.show(
          'Member limit reached',
          `This workspace has ${result.current}/${result.limit} members on the ${result.planName} plan. Upgrade to invite more.`,
          result.upgradeAction,
          result.upgradePlan
        );
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="flex flex-col gap-4 py-4 md:py-6">
      <div className="px-4 lg:px-6">
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            setActiveTab(value as 'members' | 'invitations')
          }
          className="w-full"
        >
          <div className="flex items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="members">Team Members</TabsTrigger>
              <TabsTrigger value="invitations">Pending Invitations</TabsTrigger>
            </TabsList>
            {canInvite ? (
              <>
                <Button size="sm" onClick={() => void handleInviteClick()}>
                  Invite
                </Button>
                <WorkspaceInviteDialog
                  open={inviteDialog.open}
                  onOpenChange={inviteDialog.onOpenChange}
                  email={inviteDialog.draft.email}
                  role={inviteDialog.draft.role}
                  roles={DEFAULT_INVITE_ROLES}
                  isPending={inviteDialog.isPending}
                  onEmailChange={(email) =>
                    inviteDialog.setDraft((current) => ({ ...current, email }))
                  }
                  onRoleChange={(role) =>
                    inviteDialog.setDraft((current) => ({ ...current, role }))
                  }
                  onSubmit={() => {
                    void inviteDialog.onSubmit();
                  }}
                />
              </>
            ) : null}
          </div>

          <TabsContent value="members" className="mt-4">
            <WorkspaceMembersTable {...membersTablePropsWithRole} />
          </TabsContent>

          <TabsContent value="invitations" className="mt-4">
            <WorkspaceInvitationsTable
              {...invitationsTableProps}
              canManageInvitations={capabilities.canInviteMembers}
            />
          </TabsContent>
        </Tabs>
      </div>
      <UpgradePromptDialog {...upgradePrompt.dialogProps} />
    </div>
  );
}
